import { motion } from 'framer-motion'
import { Map, MessageCircle, MoreHorizontal } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../Contexts/AuthContext'
import { useMobileMenu } from '../../Contexts/MobileMenuContext'
import { markerNotificationApi } from '../../api/markerNotificationApi'
import { useTheme } from '../../theme/hooks'
import { useNotifications } from '../shared/Notifications/useNotifications'

type Tab = {
  key: string
  label: string
  icon: typeof Map
  path?: string
  action?: () => void
}

const MobileTabBar = () => {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { toggleMoreSheet, closeMoreSheet } = useMobileMenu()

  const { stats } = useNotifications({
    userId: user?.id,
    apiEndpoint: '/notifications',
    fetchNotifications: markerNotificationApi.getUserNotifications,
    fetchStats: markerNotificationApi.getNotificationStats,
    markAsRead: markerNotificationApi.markAsRead,
    markAllAsRead: markerNotificationApi.markAllAsRead,
    dismissNotification: markerNotificationApi.dismissNotification,
    dismissAllNotifications: markerNotificationApi.dismissAllNotifications,
    notificationType: 'marker_assigned',
  })

  const tabs: Tab[] = [
    { key: 'map', label: 'Carte', icon: Map, path: '/' },
    {
      key: 'messages',
      label: 'Messages',
      icon: MessageCircle,
      path: '/messages',
    },
    {
      key: 'more',
      label: 'Plus',
      icon: MoreHorizontal,
      action: toggleMoreSheet,
    },
  ]

  const getActiveTab = (): string => {
    const path = location.pathname
    if (path === '/' || path === '') return 'map'
    if (path.startsWith('/messages')) return 'messages'
    return ''
  }

  const activeTab = getActiveTab()

  // Cacher la tab bar sur les pages qui ont leur propre navigation mobile
  if (location.pathname.startsWith('/damage')) {
    return null
  }

  const handleTabPress = (tab: Tab) => {
    if (tab.action) {
      tab.action()
    } else if (tab.path) {
      closeMoreSheet()
      navigate(tab.path)
    }
  }

  return (
    <div
      className="pb-safe fixed bottom-0 left-0 right-0 z-50 flex justify-around border-t md:hidden"
      style={{
        backgroundColor: theme.surface.primary,
        borderTopColor: theme.border.primary,
      }}
    >
      {tabs.map(tab => {
        const isActive = activeTab === tab.key
        const Icon = tab.icon

        return (
          <button
            key={tab.key}
            onClick={() => handleTabPress(tab)}
            className="relative flex flex-1 flex-col items-center py-2"
          >
            <div className="relative">
              <motion.div
                animate={{ scale: isActive ? 1.1 : 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              >
                <Icon
                  className="h-6 w-6"
                  style={{
                    color: isActive ? theme.text.link : theme.text.secondary,
                  }}
                />
              </motion.div>

              {tab.key === 'messages' && stats.unread > 0 && (
                <span
                  className="absolute -right-2 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold"
                  style={{
                    backgroundColor: theme.status.error.icon,
                    color: theme.text.inverse,
                  }}
                >
                  {stats.unread > 99 ? '99+' : stats.unread}
                </span>
              )}
            </div>

            <span
              className="mt-0.5 text-[10px]"
              style={{
                color: isActive ? theme.text.link : theme.text.secondary,
              }}
            >
              {tab.label}
            </span>

            {isActive && (
              <motion.div
                layoutId="activeTabIndicator"
                className="absolute top-0 h-0.5 w-8 rounded-full"
                style={{ backgroundColor: theme.text.link }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

export default MobileTabBar
